import React, { useEffect } from 'react';
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  HStack,
  VStack,
  Text,
  Badge,
  Tooltip,
  useColorModeValue,
  Box,
  Flex,
  Spacer,
  Heading,
} from '@chakra-ui/react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { FiX, FiSettings, FiDownload, FiRefreshCw, FiLock, FiUnlock } from 'react-icons/fi';
import { useDebugStore } from '../../../stores/debugStore';

// Import sub-components
import SkillTracker from './components/SkillTracker';
import ProfileViewer from './components/ProfileViewer';
import ConceptDetectionLog from './components/ConceptDetectionLog';
import PromptViewer from './components/PromptViewer';
import TeachingStrategyIndicator from './components/TeachingStrategyIndicator';
import DebugControls from './components/DebugControls';
import EventLog from './components/EventLog';

// Import hooks
import { usePersonalizationData } from './hooks/usePersonalizationData';
import { useWebSocketDebug } from './hooks/useWebSocketDebug';

import styles from './PersonalizationDebugPanel.module.css';

interface ArtifactDebugPanelProps {
  onClose: () => void;
}

// Small error boundary to isolate panel failures from chat UI
class SmallErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; message?: string }>
{
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(err: Error) {
    return { hasError: true, message: err?.message || 'Panel error' };
  }
  componentDidCatch(error: Error) {
    console.error('[PersonalizationDebugPanel] Error:', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box p={4}>
          <Heading size="sm" mb={2}>Debug Panel Error</Heading>
          <Text fontSize="sm" color="gray.500">{this.state.message}</Text>
          <Text fontSize="xs" color="gray.500" mt={2}>The chat UI continues unaffected.</Text>
        </Box>
      );
    }
    return this.props.children as any;
  }
}

const ArtifactDebugPanelInner: React.FC<ArtifactDebugPanelProps> = ({
  onClose,
}) => {
  const {
    activeTab,
    setActiveTab,
    isPinned,
    setPinned,
    exportDebugData,
    autoRefresh,
  } = useDebugStore();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.900');

  // Initialize data fetching and WebSocket listeners
  const { refreshAll, isLoading } = usePersonalizationData();
  useWebSocketDebug();

  // Auto-refresh data when panel opens
  useEffect(() => {
    if (autoRefresh) {
      refreshAll();
    }
  }, [autoRefresh, refreshAll]);

  const handleTabChange = (index: number) => {
    const tabs = ['concepts', 'skills', 'profile', 'prompt', 'events', 'controls'];
    setActiveTab(tabs[index]);
  };

  return (
    <Box h="100vh" display="flex" flexDirection="column" bg={bgColor}>
      {/* Header */}
      <Box
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={headerBg}
      >
        <Flex align="center">
          <VStack align="start" spacing={0}>
            <HStack>
              <Heading size="md">Personalization Debug</Heading>
              <Badge colorScheme="purple" variant="subtle">
                Artifact View
              </Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              Real-time learning analytics
            </Text>
          </VStack>
          <Spacer />
          <HStack spacing={2}>
            <Tooltip label={isPinned ? "Unlock panel" : "Lock panel open"}>
              <IconButton
                aria-label="Lock panel"
                icon={isPinned ? <FiLock /> : <FiUnlock />}
                size="sm"
                variant="ghost"
                onClick={() => setPinned(!isPinned)}
              />
            </Tooltip>
            <Tooltip label="Refresh all data">
              <IconButton
                aria-label="Refresh data"
                icon={<FiRefreshCw />}
                size="sm"
                variant="ghost"
                onClick={refreshAll}
                isLoading={isLoading}
              />
            </Tooltip>
            <Tooltip label="Export debug data (Cmd+Shift+E)">
              <IconButton
                aria-label="Export data"
                icon={<FiDownload />}
                size="sm"
                variant="ghost"
                onClick={exportDebugData}
              />
            </Tooltip>
            <Tooltip label="Settings">
              <IconButton
                aria-label="Settings"
                icon={<FiSettings />}
                size="sm"
                variant="ghost"
                onClick={() => setActiveTab('controls')}
              />
            </Tooltip>
            <Tooltip label="Close panel (Esc)">
              <IconButton
                aria-label="Close panel"
                icon={<FiX />}
                size="sm"
                variant="ghost"
                onClick={onClose}
              />
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      {/* Main Content */}
      <Tabs
        index={['concepts', 'skills', 'profile', 'prompt', 'events', 'controls'].indexOf(activeTab)}
        onChange={handleTabChange}
        variant="line"
        colorScheme="purple"
        flex={1}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <TabList px={4} borderBottomWidth="1px" borderColor={borderColor}>
          <Tab fontSize="sm">
            Concepts
            <Badge ml={2} colorScheme="green" variant="subtle" fontSize="xs">
              Live
            </Badge>
          </Tab>
          <Tab fontSize="sm">Skills</Tab>
          <Tab fontSize="sm">Profile</Tab>
          <Tab fontSize="sm">Prompt</Tab>
          <Tab fontSize="sm">
            Events
            <Badge ml={2} colorScheme="orange" variant="subtle" fontSize="xs">
              Live
            </Badge>
          </Tab>
          <Tab fontSize="sm">Controls</Tab>
        </TabList>

        <TabPanels flex={1} overflow="hidden">
          <TabPanel h="100%" p={0}>
            <PanelGroup direction="vertical">
              <Panel defaultSize={60} minSize={30}>
                <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
                  <ConceptDetectionLog />
                </Box>
              </Panel>
              <PanelResizeHandle className={styles.resizeHandle} />
              <Panel defaultSize={40} minSize={20}>
                <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
                  <TeachingStrategyIndicator />
                </Box>
              </Panel>
            </PanelGroup>
          </TabPanel>

          <TabPanel h="100%" p={0}>
            <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
              <SkillTracker />
            </Box>
          </TabPanel>

          <TabPanel h="100%" p={0}>
            <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
              <ProfileViewer />
            </Box>
          </TabPanel>

          <TabPanel h="100%" p={0}>
            <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
              <PromptViewer />
            </Box>
          </TabPanel>

          <TabPanel h="100%" p={0}>
            <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
              <EventLog />
            </Box>
          </TabPanel>

          <TabPanel h="100%" p={0}>
            <Box h="100%" overflowY="auto" p={4} className={styles.customScrollbar}>
              <DebugControls onClose={onClose} />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const ArtifactDebugPanel: React.FC<ArtifactDebugPanelProps> = (props) => (
  <SmallErrorBoundary>
    <ArtifactDebugPanelInner {...props} />
  </SmallErrorBoundary>
);

export default ArtifactDebugPanel;
